import type { AudioCaptureOptions } from 'livekit-client'

import { getLocalStorageItem, setLocalStorageItem } from '@/src/helpers/storage'

/**
 * Режим серверного шумодава для исходящего микрофона.
 *
 * `off` — в комнату уходит сырой захват с браузерным шумоподавлением.
 * `on` — PCM уходит на сервис ноды и обратно, публикуем уже чистый трек.
 */
export type NoiseFilterMode = 'off' | 'on'

/**
 * Состояние применения выбранного режима к опубликованному треку.
 */
export type NoiseFilterApplyStatus = 'idle' | 'pending' | 'ready' | 'unsupported' | 'error'

/**
 * Пункт списка шумодава в меню микрофона.
 */
export interface NoiseFilterOption {
  value: NoiseFilterMode
  label: string
}

export const NOISE_FILTER_STORAGE_KEY = 'lk-noise-filter-mode'

/**
 * По умолчанию выключен: круг через сервис добавляет задержку,
 * человек включает его сам, когда готов.
 */
export const DEFAULT_NOISE_FILTER_MODE: NoiseFilterMode = 'off'

export const NOISE_FILTER_OPTIONS: NoiseFilterOption[] = [
  { value: 'off', label: 'Без шумодава' },
  { value: 'on', label: 'Шумодав' },
]

const VALID_NOISE_FILTER_MODES = new Set<NoiseFilterMode>(['off', 'on'])

/**
 * Старые клиентские значения не должны внезапно включить серверный круг.
 *
 * `deepfilter` на stage был дефолтом WASM. После выкладки это `off`,
 * иначе у всех, кто уже заходил, появится лишний круг «из ниоткуда».
 */
const LEGACY_MODE_MAP: Record<string, NoiseFilterMode> = {
  none: 'off',
  deepfilter: 'off',
  krisp: 'off',
  off: 'off',
  on: 'on',
}

/**
 * Проверяет, что значение — известный режим шумодава.
 */
export function isNoiseFilterMode(value: unknown): value is NoiseFilterMode {
  return typeof value === 'string' && VALID_NOISE_FILTER_MODES.has(value as NoiseFilterMode)
}

/**
 * Нормализует сохранённое или пользовательское значение к безопасному режиму.
 */
export function normalizeNoiseFilterMode(value: unknown): NoiseFilterMode {
  if (typeof value === 'string' && value in LEGACY_MODE_MAP) {
    return LEGACY_MODE_MAP[value]
  }

  if (isNoiseFilterMode(value)) {
    return value
  }

  return DEFAULT_NOISE_FILTER_MODE
}

/**
 * Читает выбранный режим из localStorage.
 */
export function loadNoiseFilterMode(): NoiseFilterMode {
  if (typeof window === 'undefined') {
    return DEFAULT_NOISE_FILTER_MODE
  }

  return normalizeNoiseFilterMode(getLocalStorageItem<unknown>(NOISE_FILTER_STORAGE_KEY))
}

/**
 * Сохраняет режим и уведомляет другие компоненты через `storage`.
 *
 * Своё окно не получает нативный `storage`, поэтому дополнительно шлём
 * одноимённый Event — как для virtual background.
 */
export function saveNoiseFilterMode(mode: NoiseFilterMode): NoiseFilterMode {
  const normalized = normalizeNoiseFilterMode(mode)

  if (typeof window === 'undefined') {
    return normalized
  }

  setLocalStorageItem(NOISE_FILTER_STORAGE_KEY, normalized)
  window.dispatchEvent(new Event('storage'))

  return normalized
}

/**
 * Собирает AudioCaptureOptions под выбранный режим.
 *
 * Для серверного шумодава оставляем браузерную эхокомпенсацию: сервис
 * слышит только микрофон и чужие голоса сам не вычтет. Браузерный NS
 * выключаем, чтобы не накладывать два подавителя. AGC оставляем: он
 * нормализует уровень входа до сервера, иначе участники с тихими
 * микрофонами едва слышны в комнате.
 */
export function getAudioCaptureOptionsForNoiseFilter(
  mode: NoiseFilterMode,
  deviceId?: string,
): AudioCaptureOptions {
  const normalized = normalizeNoiseFilterMode(mode)
  const unsupportedReason = getNoiseFilterUnsupportedReason(normalized)
  const useServer = unsupportedReason ? false : normalized === 'on'

  return {
    ...(deviceId ? { deviceId } : {}),
    echoCancellation: true,
    autoGainControl: true,
    noiseSuppression: !useServer,
  }
}

/**
 * Проверяет, хватает ли браузеру AudioWorklet и WebSocket для круга через сервис.
 */
export function isServerNoiseFilterSupported(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  if (typeof WebSocket === 'undefined') {
    return false
  }

  const AudioContextCtor =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

  if (!AudioContextCtor) {
    return false
  }

  return 'audioWorklet' in AudioContextCtor.prototype
}

/**
 * Возвращает человекочитаемую причину, почему режим сейчас недоступен.
 */
export function getNoiseFilterUnsupportedReason(mode: NoiseFilterMode): string | null {
  if (mode === 'on' && !isServerNoiseFilterSupported()) {
    return 'Шумодав недоступен в этом браузере: нужны AudioWorklet и WebSocket'
  }

  return null
}

/**
 * Собирает адрес WebSocket шумодава на той же ноде, что LiveKit.
 *
 * Клиент уже знает `livekit_url`. Путь `/nf` на этом хосте Caddy отдаёт
 * в контейнер сервиса. Query не копируем: токен добавим отдельно.
 */
export function livekitUrlToNoiseFilterUrl(livekitUrl: string): string {
  const parsed = new URL(livekitUrl)

  parsed.pathname = '/nf'
  parsed.search = ''
  parsed.hash = ''

  return parsed.toString()
}

/**
 * Пишет диагностическое событие шумодава без аудиоданных.
 */
export function logNoiseFilterEvent(event: string, details: Record<string, unknown> = {}): void {
  console.info('[noise-filter]', event, details)
}
